import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, FileText, User, Eye, ChevronDown, MessageSquare, Clock, CalendarDays, MapPin, Users } from "lucide-react";

import { useI18n } from "@/i18n/I18nContext";
import { text } from "@/lib/memberDerive";
import { shortName } from "../navSections";
import { useMemberProfile } from "../useMemberProfile";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Field, V3Actions, AsideCard, StatusBanner, DetailRow, DetailList, SettingRow, V3Switch, ChoiceCard, ReadingPreview, V3_PRIMARY, V3_SECONDARY } from "../components/v3";

export const INTEREST_KEYS = ["soziales", "sport", "kultur", "bildung", "freizeit", "regional"];
const INTEREST_ICONS = { soziales: MessageSquare, sport: Clock, kultur: CalendarDays, bildung: FileText, freizeit: MapPin, regional: Users };

// Kept for Profile.jsx: same Core categories as the V3 choice cards.
export function InterestPicker({ value, onChange, disabled }) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-2" role="group" aria-label="Interessen">
      {INTEREST_KEYS.map((k) => {
        const on = value.includes(k);
        return <ChoiceCard key={k} icon={INTEREST_ICONS[k]} label={t(`member.categories.${k}`)} selected={on} disabled={disabled} testid={`member-interest-${k}`}
          onToggle={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])} />;
      })}
    </div>
  );
}

const busy = (on) => on && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />;
const firstName = (name) => (typeof name === "string" ? name.trim().split(/\s+/)[0] : "") || "";

function Responsible({ prof }) {
  const n = shortName(prof.name);
  return <AsideCard icon={User} tone="white" title="Verantwortung" text={n ? `${n} · Mitglied` : "Mitglied"} testid="setup-responsible" />;
}

const READONLY_HINT = "Kommt aus deinem Konto. Ändern ist noch nicht verfügbar.";

// M03 / M36 – first step; only "Kurz über mich" is stored by the Core (name, city, country, photo: contract C.2).
function ProfileStep({ p, prof }) {
  const navigate = useNavigate();
  const [bio, setBio] = useState(text(prof.bio));
  const first = !text(prof.bio) && !(prof.interests || []).length;
  const next = async () => {
    if (bio === text(prof.bio) || await p.patch({ bio })) navigate("/einrichtung/interessen");
  };
  return (<>
    <V3Title title="Dein Profil" testid="setup-title" intro={first ? "Beginne mit deinem Namen und deiner Stadt. Ein Foto ist freiwillig." : "So können andere dich kennenlernen. Freiwillige Angaben kannst du später ergänzen."} />
    <V3Columns layout="app" aside={<><AsideCard icon={FileText} title="Ausgewählter Vorgang" text={firstName(prof.name) || "Dein Profil"} /><Responsible prof={prof} /></>} main={
      <div data-m={first ? "M36" : "M03"} data-testid="setup-profile">
        <V3Field id="setup-name" label="Anzeigename" value={firstName(prof.name)} readOnly hint={READONLY_HINT} />
        <V3Field id="setup-city" label="Stadt" value={prof.city || "Noch nicht angegeben"} readOnly hint={READONLY_HINT} />
        {first
          ? <V3Field id="setup-country" label="Land" value="Noch nicht verfügbar" readOnly hint="Das Land kann der gemeinsame Core noch nicht speichern." />
          : <V3Field id="setup-photo" label="Profilfoto" value="Freiwillig · Initialen sind möglich" readOnly hint="Ein eigenes Foto ist noch nicht verfügbar; es werden deine Initialen gezeigt." />}
        <V3Field id="setup-bio" testid="setup-bio" label="Kurz über mich" multiline value={bio} onChange={(e) => setBio(e.target.value)} maxLength={300}
          placeholder="Zum Beispiel: Ich mag Spaziergänge und gute Gespräche." className="h-[65px] lg:h-[52px]" hint="Freiwillig, höchstens 300 Zeichen. Keine Wohnadresse und keine medizinischen Angaben nötig." />
        <V3Actions>
          <button type="button" onClick={next} disabled={p.saving} data-testid="setup-next" className={V3_PRIMARY}>{busy(p.saving)}{first ? "Weiter" : "Weiter zu Interessen"}</button>
          <Link to="/einrichtung/interessen" data-testid="setup-skip" className={V3_SECONDARY}>Später ergänzen</Link>
        </V3Actions>
      </div>
    } />
  </>);
}

// M37 – V3 lists other topics than the Core categories (contract A.4.5); the Core categories are shown.
function InterestStep({ p, prof }) {
  const navigate = useNavigate();
  const [interests, setInterests] = useState(prof.interests || []);
  const save = async () => { if (await p.patch({ interests })) navigate("/einrichtung/ansicht"); };
  return (<>
    <V3Title title="Interessen auswählen" testid="setup-title" intro="Wähle Themen, die dich interessieren. Du kannst sie später ändern." />
    <V3Columns layout="detail" aside={<><AsideCard icon={FileText} title="Dein Bereich" text={interests.length ? `${interests.length} ausgewählt` : "Noch nichts ausgewählt"} testid="setup-interest-count" /><Responsible prof={prof} /></>} main={
      <div data-m="M37" data-testid="setup-interests">
        <InterestPicker value={interests} onChange={setInterests} disabled={p.saving} />
        <div className="mt-[22px] lg:mt-[17px]">
          <V3Field id="setup-own-interest" label="Eigenes Interesse (freiwillig)" disabled placeholder="Noch nicht verfügbar"
            hint="Eigene Interessen kann der gemeinsame Core noch nicht speichern." />
        </div>
        <V3Actions>
          <button type="button" onClick={save} disabled={p.saving} data-testid="setup-next" className={V3_PRIMARY}>{busy(p.saving)}Interessen übernehmen</button>
          <Link to="/einrichtung/ansicht" data-testid="setup-skip" className={V3_SECONDARY}>Überspringen</Link>
        </V3Actions>
      </div>
    } />
  </>);
}

function PreviewAside({ draft, next }) {
  return (<>
    <AsideCard icon={Eye} title="Deine Lesevorschau" text="Eine gut lesbare Ansicht unterstützt deinen eigenen nächsten Schritt." />
    <ReadingPreview large={draft.large_text} contrast={draft.high_contrast}
      action={<Link to={next} data-testid="setup-preview-next" className={`${V3_PRIMARY} lg:w-full`}>Nächsten Schritt ansehen</Link>} />
  </>);
}

const onOff = (v) => (v ? "Ein" : "Aus");

// M04 (setup) and M38 (settings): draft first, stored together via "Darstellung übernehmen".
function ViewStep({ p, settings }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => ({ large_text: !!p.a11y.large_text, high_contrast: !!p.a11y.high_contrast, reduce_motion: !!p.a11y.reduce_motion }));
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const next = settings ? "/einstellungen" : "/einrichtung/privatsphaere";
  const apply = async () => { if (await p.applyAcc(draft) && !settings) navigate(next); };
  return (<>
    <V3Title title="Deine Ansicht" testid="setup-title" intro={settings ? "Vergleiche die Leseansicht, bevor du die Darstellung übernimmst." : "Stelle die Ansicht so ein, dass sie für dich gut lesbar ist."} />
    <V3Columns layout="settings" aside={<PreviewAside draft={draft} next={next} />} main={
      <div data-m={settings ? "M38" : "M04"} data-testid="setup-view" className="space-y-[12px]">
        <SettingRow title="Schriftgrösse" value={draft.large_text ? "Gross" : "Standard"} testid="setup-text-size">
          <span className="relative">
            <select aria-label="Schriftgrösse" value={draft.large_text ? "gross" : "standard"} onChange={(e) => set("large_text")(e.target.value === "gross")} data-testid="member-acc-large_text"
              className="h-[40px] cursor-pointer appearance-none rounded-[8px] border border-v3-line bg-white pl-3 pr-9 text-[14px] text-v3-ink">
              <option value="standard">Standard</option><option value="gross">Gross</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-v3-muted" aria-hidden="true" />
          </span>
        </SettingRow>
        <SettingRow title={settings ? "Kontrast" : "Stärkerer Kontrast"} value={settings ? (draft.high_contrast ? "Hoch" : "Standard") : onOff(draft.high_contrast)} testid="setup-contrast">
          <V3Switch checked={draft.high_contrast} onChange={set("high_contrast")} label={settings ? "Kontrast hoch" : "Stärkerer Kontrast"} testid="member-acc-high_contrast" />
        </SettingRow>
        <SettingRow title="Bewegung reduzieren" value={onOff(draft.reduce_motion)} testid="setup-motion">
          <V3Switch checked={draft.reduce_motion} onChange={set("reduce_motion")} label="Bewegung reduzieren" testid="member-acc-reduce_motion" />
        </SettingRow>
        <SettingRow title="Vereinfachte Ansicht" value="Aus" note="Noch nicht verfügbar – der gemeinsame Core speichert diese Einstellung noch nicht." testid="setup-simplified">
          <V3Switch checked={false} onChange={() => {}} disabled label="Vereinfachte Ansicht (noch nicht verfügbar)" describedBy="setup-simplified-note" testid="member-acc-simplified" />
        </SettingRow>
        <V3Actions>
          <button type="button" onClick={apply} disabled={p.saving} data-testid="setup-next" className={V3_PRIMARY}>{busy(p.saving)}Darstellung übernehmen</button>
          <Link to={settings ? "/einstellungen" : "/einrichtung/interessen"} data-testid="setup-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </div>
    } />
  </>);
}

// M39 – the two privacy switches the Core stores; city-only and the private Access Pass are fixed rules.
function PrivacyStep({ p }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(p.privacy);
  const save = async () => { if (await p.savePrefs({ ...p.prefs, privacy: draft })) navigate("/einrichtung/fertig"); };
  return (<>
    <V3Title title="Deine Privatsphäre" testid="setup-title" intro="Prüfe, welche Angaben andere Mitglieder von dir sehen können." />
    <V3Columns layout="settings" aside={<PreviewAside draft={p.a11y} next="/einrichtung/fertig" />} main={
      <div data-m="M39" data-testid="setup-privacy" className="space-y-[12px]">
        <SettingRow title="Profil" value={draft.discoverable ? "Nur Mitglieder" : "Im Mitgliederverzeichnis verborgen"} testid="setup-privacy-profile">
          <V3Switch checked={draft.discoverable} onChange={(v) => setDraft((d) => ({ ...d, discoverable: v }))} label="Im Mitgliederverzeichnis auffindbar" testid="member-privacy-discoverable" />
        </SettingRow>
        <SettingRow title="Kontaktanfragen" value={draft.allow_contact_requests ? "Erlauben" : "Nicht erlauben"} testid="setup-privacy-contact">
          <V3Switch checked={draft.allow_contact_requests} onChange={(v) => setDraft((d) => ({ ...d, allow_contact_requests: v }))} label="Kontaktanfragen erlauben" testid="member-privacy-allow" />
        </SettingRow>
        <SettingRow title="Ort" value="Nur Stadt" testid="setup-privacy-place" />
        <SettingRow title="Access Pass" value="Privat · Gesondertes Teilen möglich" testid="setup-privacy-pass" />
        <V3Actions>
          <button type="button" onClick={save} disabled={p.saving} data-testid="setup-next" className={V3_PRIMARY}>{busy(p.saving)}Einstellungen speichern</button>
          <Link to="/einrichtung/ansicht" data-testid="setup-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </div>
    } />
  </>);
}

// M40 – first start without content: only real ways forward, no invented suggestions.
function FirstStart({ prof }) {
  return (<>
    <V3Title title="Erster Start ohne Inhalte" testid="setup-title" intro="Dein Konto ist eingerichtet. Wähle deinen ersten eigenen Schritt." />
    <V3Columns layout="detail" aside={<><AsideCard icon={FileText} title="Dein Bereich" text="Gemeinsame Interessen finden" /><Responsible prof={prof} /></>} main={
      <div data-m="M40" data-testid="setup-first-start">
        <StatusBanner tone="sand" title="Dein erster Schritt" text="Gemeinsame Interessen finden" />
        <DetailList>
          <DetailRow label="Menschen entdecken" value="Gemeinsame Interessen finden" to="/menschen" testid="first-start-people" />
          <DetailRow label="Aktivität finden" value="Termine und Zugangsangaben prüfen" to="/aktivitaeten" testid="first-start-activities" />
          <DetailRow label="Kreis ansehen" value="Beschreibung und Regeln kennenlernen" to="/kreise" testid="first-start-groups" />
          <DetailRow label="Später ergänzen" value="Dein Profil bleibt jederzeit bearbeitbar" to="/profil" testid="first-start-profile" />
        </DetailList>
        <V3Actions>
          <Link to="/aktivitaeten" data-testid="setup-next" className={V3_PRIMARY}>Aktivitäten entdecken</Link>
          <Link to="/einrichtung/privatsphaere" data-testid="setup-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </div>
    } />
  </>);
}

const STEPS = { profil: ProfileStep, interessen: InterestStep, ansicht: ViewStep, privatsphaere: PrivacyStep, fertig: FirstStart, darstellung: ViewStep };

// M03/M04/M36–M40 and M38: voluntary setup; every step can be skipped and changed later.
export default function Setup({ step = "profil" }) {
  const p = useMemberProfile();
  const prof = p.data && p.data.profile;
  if (p.error) return <ConnectionError onRetry={p.load} />;
  if (!prof) return <Loading />;
  const Step = STEPS[step] || ProfileStep;
  return <div data-testid="member-setup"><Step key={step} p={p} prof={prof} settings={step === "darstellung"} /></div>;
}
